/**
 * 🛒 Marketplace — friend-only pet gear trading.
 *
 * Three tabs:
 *  • Friends — listings from your friends (buy)
 *  • Sell    — pick from your tradeable inventory and post
 *  • Mine    — your own active + history listings (cancel active)
 *
 * Backed by /api/marketplace/state (single payload) + create/buy/cancel.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CUR_SYMBOL = { spirit: "🍚", od: "🪙" };
const CUR_LABEL = { spirit: "Brawler Spirit", od: "Omni Dollars" };
const _cur = (c) => CUR_SYMBOL[c] || (c ? `(${c})` : "");

export default function MarketplacePage() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("friends");
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/marketplace/state`);
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load marketplace.");
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    refresh();
  }, [authLoading, user, refresh]);

  if (authLoading) return null;
  if (!user) {
    return (
      <div>
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400">
          🔒 Sign in to access the marketplace.
        </div>
        <Footer />
      </div>
    );
  }
  if (!state) {
    return (
      <div>
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-500">Loading…</div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="max-w-5xl mx-auto p-4 sm:p-8 pb-32" data-testid="marketplace-page">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl mb-1">🛒 Marketplace</h1>
            <p className="text-xs text-neutral-500">
              Trade pet gear with your friends. {state.house_cut_pct}% house fee on sale ·
              {" "}{state.listing_fee_spirit} 🍚 to post (refunded on sale/cancel).
            </p>
          </div>
          <div className="flex gap-2">
            <Wallet icon="🍚" label="Brawler Spirit" value={state.wallet.brawler_spirit} testId="market-wallet-spirit" />
            <Wallet icon="🪙" label="Omni Dollars" value={state.wallet.omni_dollars} testId="market-wallet-od" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-900 mb-4 overflow-x-auto">
          <TabBtn id="friends" active={tab} onClick={setTab} count={state.friend_listings.length}>👥 Friends</TabBtn>
          <TabBtn id="sell" active={tab} onClick={setTab} count={state.inventory.length}>🏷️ Sell</TabBtn>
          <TabBtn id="mine" active={tab} onClick={setTab} count={state.my_listings.filter(l => l.status === "active").length}>📦 Mine</TabBtn>
          <TabBtn id="leaderboard" active={tab} onClick={setTab} count={null}>🏆 Top</TabBtn>
        </div>

        {tab === "friends" && (
          <FriendListings
            listings={state.friend_listings}
            wallet={state.wallet}
            friendCount={state.friend_count}
            busy={busy}
            onBuy={async (id) => {
              setBusy(true);
              try {
                await axios.post(`${API}/marketplace/listings/${id}/buy`);
                toast.success("🛒 Purchase complete!");
                await refresh();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Buy failed.");
              }
              setBusy(false);
            }}
          />
        )}

        {tab === "sell" && (
          <SellPanel
            inventory={state.inventory}
            wallet={state.wallet}
            listingFee={state.listing_fee_spirit}
            maxPrice={state.max_price}
            busy={busy}
            onPost={async (payload) => {
              setBusy(true);
              try {
                await axios.post(`${API}/marketplace/listings`, payload);
                toast.success("🛒 Listing posted!");
                await refresh();
                setTab("mine");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Listing failed.");
              }
              setBusy(false);
            }}
          />
        )}

        {tab === "mine" && (
          <MyListings
            listings={state.my_listings}
            busy={busy}
            onCancel={async (id) => {
              setBusy(true);
              try {
                await axios.post(`${API}/marketplace/listings/${id}/cancel`);
                toast.success("🛒 Listing cancelled — item + 🍚 refunded.");
                await refresh();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Cancel failed.");
              }
              setBusy(false);
            }}
          />
        )}

        {tab === "leaderboard" && <Leaderboard />}
      </div>
      <Footer />
    </div>
  );
}

function Wallet({ icon, label, value, testId }) {
  return (
    <div className="border border-neutral-800 bg-black/40 px-3 py-1.5 flex items-center gap-2" data-testid={testId} title={label}>
      <span className="text-base">{icon}</span>
      <span className="font-mono tabular-nums text-sm font-bold">{value.toLocaleString()}</span>
    </div>
  );
}

function TabBtn({ id, active, onClick, children, count }) {
  const is = active === id;
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`market-tab-${id}`}
      className={`px-3 py-2 text-[11px] uppercase tracking-[0.2em] font-bold transition border-b-2 -mb-px shrink-0 ${
        is ? "text-[#FCD34D] border-[#FCD34D]" : "text-neutral-500 border-transparent hover:text-white"
      }`}
    >
      {children}
      {count !== null && count !== undefined && (
        <span className="text-[9px] opacity-70 ml-1">({count})</span>
      )}
    </button>
  );
}

// ─── Leaderboard tab ─────────────────────────────────────────────────
function Leaderboard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/marketplace/leaderboard?days=${days}`);
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) toast.error(e?.response?.data?.detail || "Couldn't load leaderboard.");
      }
    })();
    return () => { cancel = true; };
  }, [days]);

  if (!data) {
    return <div className="text-center text-neutral-500 text-xs py-8">Loading…</div>;
  }
  const noActivity = data.sellers.length === 0 && data.buyers.length === 0;

  return (
    <div className="space-y-4" data-testid="market-leaderboard">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FCD34D]">
          🏆 Last {data.days} day{data.days === 1 ? "" : "s"}
        </div>
        <div className="flex gap-1">
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`leaderboard-days-${d}`}
              className={`px-2 py-1 text-[9px] uppercase tracking-widest font-bold border transition ${
                days === d ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "text-neutral-500 border-neutral-800 hover:text-white"
              }`}
            >
              {d === 1 ? "Today" : `${d}d`}
            </button>
          ))}
        </div>
      </div>

      {/* House Burn Tracker */}
      <div className="grid grid-cols-3 gap-2 bg-gradient-to-r from-purple-950/30 to-amber-950/20 border border-[#7C3AED]/40 p-3">
        <BurnStat label="Sales" value={data.burn.total_sales} icon="🛒" />
        <BurnStat label="🍚 Burned" value={data.burn.spirit_burned} accent="text-[#FCD34D]" />
        <BurnStat label="🪙 Burned" value={data.burn.od_burned} accent="text-[#FCD34D]" />
      </div>

      {noActivity ? (
        <Empty icon="🏆" title="No friend trades yet" body="Once a friend buys or sells in this window, the leaderboard fills up here." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LBList title="🏷️ Top Sellers" rows={data.sellers} mode="sell" />
          <LBList title="🛒 Top Buyers" rows={data.buyers} mode="buy" />
        </div>
      )}
    </div>
  );
}

function BurnStat({ label, value, icon, accent }) {
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">{icon} {label}</div>
      <div className={`text-xl font-mono font-bold tabular-nums mt-0.5 ${accent || "text-white"}`}>
        {Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

function LBList({ title, rows, mode }) {
  return (
    <div className="bg-[#0F0F0F] border border-neutral-800 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FCD34D] mb-2">{title}</div>
      {rows.length === 0 ? (
        <p className="text-xs text-neutral-500">No activity yet.</p>
      ) : (
        <ol className="space-y-1">
          {rows.map((r, i) => {
            const medal = ["🥇", "🥈", "🥉"][i] || `${i + 1}.`;
            return (
              <li
                key={r.user_id || i}
                data-testid={`lb-${mode}-${i}`}
                className={`flex items-center justify-between gap-2 px-2 py-1.5 ${r.is_me ? "bg-amber-950/30 border border-[#D4AF37]" : ""}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm shrink-0 w-6 text-center">{medal}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-bold truncate">{r.name} {r.is_me && <span className="text-[#FCD34D] text-[9px]">YOU</span>}</div>
                    {r.handle && <div className="text-[9px] text-neutral-500 truncate">@{r.handle}</div>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-mono font-bold tabular-nums">{r.count}</div>
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">
                    {mode === "sell" ? "sold" : "bought"} · {Number(r.total).toLocaleString()}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// ─── Friends tab ─────────────────────────────────────────────────────
function FriendListings({ listings, wallet, friendCount, busy, onBuy }) {
  if (friendCount === 0) {
    return (
      <Empty icon="👥" title="No friends yet" body="Add friends to see their listings here." />
    );
  }
  if (listings.length === 0) {
    return (
      <Empty icon="🛒" title="No friend listings" body="None of your friends are selling right now. Check back later — or post your own in the Sell tab." />
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {listings.map((l) => {
        const have = l.currency === "spirit" ? wallet.brawler_spirit : wallet.omni_dollars;
        const canAfford = have >= l.price;
        return (
          <ListingCard key={l.listing_id} listing={l} testId={`listing-friend-${l.listing_id}`}>
            <button
              onClick={() => onBuy(l.listing_id)}
              disabled={!canAfford || busy}
              data-testid={`listing-buy-${l.listing_id}`}
              className={`w-full mt-2 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold transition ${
                canAfford
                  ? "bg-[#D4AF37] hover:bg-[#F1C84B] text-black"
                  : "bg-zinc-900 text-neutral-600 cursor-not-allowed"
              }`}
            >
              {canAfford ? `Buy · ${l.price} ${_cur(l.currency)}` : `Need ${l.price - have} more ${_cur(l.currency)}`}
            </button>
          </ListingCard>
        );
      })}
    </div>
  );
}

// ─── Sell tab ────────────────────────────────────────────────────────
function SellPanel({ inventory, wallet, listingFee, maxPrice, busy, onPost }) {
  const [pickedId, setPickedId] = useState(null);
  const [currency, setCurrency] = useState("spirit");
  const [price, setPrice] = useState("");
  const picked = useMemo(() => inventory.find((i) => i.item_id === pickedId) || null, [inventory, pickedId]);
  const canPost = !!picked && Number(price) > 0 && Number(price) <= maxPrice && wallet.brawler_spirit >= listingFee;

  if (inventory.length === 0) {
    return (
      <Empty icon="📦" title="No tradeable items yet" body="Win arena fights to earn gear drops. Soulbound items (like 🍓 Embersweet Fruit Leather) can't be listed." />
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Item picker */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FCD34D] mb-2">Pick an item</div>
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {inventory.map((it) => {
            const is = pickedId === it.item_id;
            return (
              <button
                key={it.item_id}
                onClick={() => setPickedId(it.item_id)}
                data-testid={`sell-inv-${it.item_id}`}
                className={`w-full text-left flex items-center gap-3 p-3 border-2 transition ${
                  is ? "border-[#D4AF37] bg-amber-950/15" : "border-neutral-800 bg-neutral-950 hover:border-neutral-700"
                }`}
              >
                <span className="text-2xl shrink-0 w-8 text-center">{it.icon || "🎒"}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{it.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    {it.slot} · {it.rarity} · owned {it.owned}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Form */}
      <div className="bg-[#0F0F0F] border-2 border-neutral-800 p-4">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FCD34D] mb-2">Listing details</div>
        {!picked ? (
          <p className="text-xs text-neutral-500">Pick an item from the left to continue.</p>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-neutral-900">
              <span className="text-3xl">{picked.icon}</span>
              <div>
                <div className="font-bold">{picked.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{picked.slot} · {picked.rarity}</div>
              </div>
            </div>
            <label className="block mb-2">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Currency</span>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                data-testid="sell-currency"
                className="w-full bg-black border border-neutral-800 px-2 py-1.5 text-sm mt-1"
              >
                {Object.entries(CUR_SYMBOL).map(([k, sym]) => (
                  <option key={k} value={k}>{sym} {CUR_LABEL[k]}</option>
                ))}
              </select>
            </label>
            <label className="block mb-3">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Price ({CUR_SYMBOL[currency]})</span>
              <input
                type="number"
                min={1}
                max={maxPrice}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                data-testid="sell-price"
                className="w-full bg-black border border-neutral-800 px-2 py-1.5 text-sm mt-1 font-mono tabular-nums"
              />
            </label>
            <div className="text-[10px] text-neutral-500 mb-3 leading-relaxed">
              Listing fee: <span className="text-[#FCD34D] font-bold">{listingFee} 🍚</span> (refunded on sale or cancel).
              You'll receive <span className="text-[#FCD34D] font-bold">{Math.max(0, Math.round(Number(price || 0) * 0.9))} {CUR_SYMBOL[currency]}</span> after the 10% house fee.
            </div>
            <button
              onClick={() => onPost({ item_id: picked.item_id, currency, price: Number(price) })}
              disabled={!canPost || busy}
              data-testid="sell-submit"
              className={`w-full py-2 text-[10px] uppercase tracking-[0.2em] font-bold transition ${
                canPost
                  ? "bg-[#D4AF37] hover:bg-[#F1C84B] text-black"
                  : "bg-zinc-900 text-neutral-600 cursor-not-allowed"
              }`}
            >
              🏷️ Post Listing
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Mine tab ────────────────────────────────────────────────────────
function MyListings({ listings, busy, onCancel }) {
  const active = listings.filter((l) => l.status === "active");
  const history = listings.filter((l) => l.status !== "active");
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FCD34D] mb-2">Active</div>
        {active.length === 0 ? (
          <p className="text-xs text-neutral-500">No active listings. Post one from the Sell tab.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {active.map((l) => (
              <ListingCard key={l.listing_id} listing={l} testId={`listing-mine-${l.listing_id}`}>
                <button
                  onClick={() => onCancel(l.listing_id)}
                  disabled={busy}
                  data-testid={`listing-cancel-${l.listing_id}`}
                  className="w-full mt-2 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold bg-neutral-800 hover:bg-neutral-700 text-neutral-300 transition"
                >
                  Cancel Listing
                </button>
              </ListingCard>
            ))}
          </div>
        )}
      </div>
      {history.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">History</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 opacity-70">
            {history.map((l) => (
              <ListingCard key={l.listing_id} listing={l} testId={`listing-history-${l.listing_id}`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shared card ─────────────────────────────────────────────────────
function ListingCard({ listing, children, testId }) {
  const m = listing.item_meta || {};
  const statusColor = listing.status === "active"
    ? "border-[#7C3AED]/40"
    : listing.status === "sold"
      ? "border-emerald-900/60 bg-emerald-950/10"
      : "border-zinc-900 bg-neutral-950";
  return (
    <div data-testid={testId} className={`bg-[#0F0F0F] border-2 p-3 ${statusColor}`}>
      <div className="flex items-start gap-3">
        <span className="text-3xl leading-none shrink-0">{m.icon || "🎒"}</span>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm truncate">{m.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            {m.slot} · {m.rarity}
          </div>
          <div className="mt-1 text-[11px]">
            <span className="text-[#FCD34D] font-bold font-mono tabular-nums">{listing.price.toLocaleString()} {_cur(listing.currency)}</span>
          </div>
          {listing.seller_name && (
            <div className="text-[10px] text-neutral-500 mt-0.5 truncate">
              by <span className="text-neutral-300">{listing.seller_name}</span>
              {listing.seller_handle && <span className="text-neutral-600"> @{listing.seller_handle}</span>}
            </div>
          )}
          {listing.status !== "active" && (
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold mt-1">
              {listing.status === "sold" ? "✓ Sold" : "✗ Cancelled"}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function Empty({ icon, title, body }) {
  return (
    <div className="bg-[#0F0F0F] border-2 border-neutral-900 p-8 text-center" data-testid="market-empty">
      <div className="text-4xl mb-2">{icon}</div>
      <div className="font-display text-lg">{title}</div>
      <div className="text-xs text-neutral-500 mt-1 max-w-md mx-auto leading-relaxed">{body}</div>
    </div>
  );
}
